import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { ArrowLeft, ShoppingBag, Trash2, Plus, Minus, Sparkles, ShieldCheck, Truck } from "lucide-react";

export default function ShopCart() {
  const { items, removeItem, setQty, totals } = useCart();
  const nav = useNavigate();

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 text-center" data-testid="shop-cart-empty">
        <ShoppingBag className="w-12 h-12 mx-auto text-[#E5276A]/40 mb-4" />
        <h1 className="font-display text-3xl text-[#1F3A5F]">Votre panier est vide</h1>
        <p className="text-[#4A5568] mt-3">Découvrez nos cartes viticoles imprimées en haute qualité.</p>
        <Link to="/shop" data-testid="shop-cart-empty-cta"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#E5276A] text-white font-bold hover:bg-[#C01661] transition-colors">
          Explorer la boutique →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="shop-cart-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#8B95A1] hover:text-[#E5276A]" data-testid="shop-cart-back">
        <ArrowLeft className="w-4 h-4" /> Continuer mes achats
      </Link>
      <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-4">Votre panier ({totals.count})</h1>

      <div className="mt-8 grid md:grid-cols-[1fr,360px] gap-8">
        {/* Items */}
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.variant_id} data-testid={`shop-cart-item-${it.variant_id}`}
              className="flex gap-4 p-3 bg-white border border-[#1F3A5F]/10 rounded-2xl">
              <div className="shrink-0 w-20 h-24 bg-[#FBF7F3] rounded-lg overflow-hidden">
                {it.image && <img src={it.image} alt={it.product_name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/shop/product/${it.product_id}`} className="font-bold text-[#1F3A5F] text-sm hover:text-[#E5276A] line-clamp-2 leading-snug">
                  {it.product_name}
                </Link>
                <div className="text-xs text-[#8B95A1] mt-1">{it.variant_title}</div>
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <div className="inline-flex items-center bg-[#FBF7F3] rounded-full">
                    <button onClick={() => setQty(it.variant_id, it.quantity - 1)} data-testid={`shop-cart-minus-${it.variant_id}`}
                      className="p-1.5 text-[#1F3A5F] hover:text-[#E5276A]"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="text-sm font-bold text-[#1F3A5F] w-6 text-center">{it.quantity}</span>
                    <button onClick={() => setQty(it.variant_id, it.quantity + 1)} data-testid={`shop-cart-plus-${it.variant_id}`}
                      className="p-1.5 text-[#1F3A5F] hover:text-[#E5276A]"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-display text-lg text-[#E5276A]">{(it.unit_price_eur * it.quantity).toFixed(2)} €</span>
                    <button onClick={() => removeItem(it.variant_id)} data-testid={`shop-cart-remove-${it.variant_id}`}
                      className="p-1.5 text-[#8B95A1] hover:text-[#E5276A]" title="Retirer"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="md:sticky md:top-4 self-start">
          <div className="bg-white border border-[#1F3A5F]/12 rounded-2xl p-5">
            <h2 className="font-display text-xl text-[#1F3A5F]">Récapitulatif</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#4A5568]">
                <span>Sous-total</span>
                <span className="font-bold text-[#1F3A5F]" data-testid="shop-cart-subtotal">{totals.subtotal.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-[#4A5568]">
                <span>Livraison</span>
                <span className="text-xs text-[#8B95A1]">Calculée à l&apos;étape suivante</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#1F3A5F]/10 flex justify-between items-baseline">
              <span className="font-bold text-[#1F3A5F]">Estimation totale</span>
              <span className="font-display text-2xl text-[#E5276A]">{totals.subtotal.toFixed(2)} €</span>
            </div>
            <button
              onClick={() => nav("/shop/checkout")}
              data-testid="shop-cart-checkout"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E5276A] text-white font-bold hover:bg-[#C01661] transition-colors"
            >
              Procéder au paiement →
            </button>
            <div className="mt-5 space-y-2 text-xs text-[#4A5568]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#E5276A]" /> Paiement sécurisé Stripe</div>
              <div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-[#E5276A]" /> Production & livraison locale (130+ ateliers)</div>
              <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A]" /> Impression locale, papier premium</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
